import { memo } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import FormGroup from "@mui/material/FormGroup";
import Checkbox from "@mui/material/Checkbox";

import { parseQuestionOptions } from "src-next/shared/utils/questionnaireOptions";
import type { QuestionnaireQuestionFormValues } from "../types";

export interface QuestionFieldPreviewProps {
  question: QuestionnaireQuestionFormValues;
}

const wordBreakSx = {
  wordBreak: "break-word",
  overflowWrap: "break-word",
  minWidth: 0,
} as const;

const enunciadoSx = { fontWeight: 600, mb: 0.5, ...wordBreakSx };
const descricaoSx = {
  display: "block",
  mb: 1,
  fontSize: "0.75rem",
  color: "text.secondary",
  ...wordBreakSx,
};

const QuestionFieldPreview = memo(({ question }: QuestionFieldPreviewProps) => {
  const label =
    question.type === "yesNo"
      ? question.label || "Pergunta de exemplo (Sim / Não)"
      : question.type === "multipleChoice"
        ? question.label || "Pergunta de múltipla escolha"
        : question.label || "Pergunta de texto aberto";
  const suffix = question.isRequired ? " *" : "";

  if (question.type === "yesNo") {
    return (
      <Box sx={{ minWidth: 0, overflow: "hidden" }}>
        <Typography variant="body1" sx={enunciadoSx}>
          {label}
          {suffix}
        </Typography>
        {question.helperText && (
          <Typography variant="caption" sx={descricaoSx}>
            {question.helperText}
          </Typography>
        )}
        <RadioGroup row disabled>
          <FormControlLabel value="yes" control={<Radio />} label="Sim" />
          <FormControlLabel value="no" control={<Radio />} label="Não" />
        </RadioGroup>
      </Box>
    );
  }

  if (question.type === "multipleChoice") {
    const options = parseQuestionOptions(question.optionsCsv);
    const shouldAllowMultipleAnswers = Boolean(question.allowMultipleAnswers);

    return (
      <Box sx={{ minWidth: 0, overflow: "hidden" }}>
        <Typography variant="body1" sx={enunciadoSx}>
          {label}
          {suffix}
        </Typography>
        {question.helperText && (
          <Typography variant="caption" sx={descricaoSx}>
            {question.helperText}
          </Typography>
        )}
        {shouldAllowMultipleAnswers ? (
          <FormGroup>
            {options.map((option) => (
              <FormControlLabel
                key={option}
                control={<Checkbox disabled />}
                label={option}
                sx={{ "& .MuiFormControlLabel-label": wordBreakSx }}
              />
            ))}
            {options.length === 0 && (
              <Typography variant="caption" color="text.disabled">
                Defina as opções no campo abaixo para ver o exemplo completo.
              </Typography>
            )}
          </FormGroup>
        ) : (
          <RadioGroup disabled>
            {options.map((option) => (
              <FormControlLabel
                key={option}
                value={option}
                control={<Radio />}
                label={option}
                sx={{ "& .MuiFormControlLabel-label": wordBreakSx }}
              />
            ))}
            {options.length === 0 && (
              <Typography variant="caption" color="text.disabled">
                Defina as opções no campo abaixo para ver o exemplo completo.
              </Typography>
            )}
          </RadioGroup>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ minWidth: 0, overflow: "hidden" }}>
      <Typography variant="body1" sx={enunciadoSx}>
        {label}
        {suffix}
      </Typography>
      {question.helperText && (
        <Typography variant="caption" sx={descricaoSx}>
          {question.helperText}
        </Typography>
      )}
      <TextField
        label="Resposta"
        placeholder="Escreva aqui..."
        fullWidth
        multiline={question.type === "longText"}
        minRows={question.type === "longText" ? 5 : 1}
        InputProps={{ readOnly: true }}
      />
    </Box>
  );
});

QuestionFieldPreview.displayName = "QuestionFieldPreview";

export default QuestionFieldPreview;
